<template>
  <div class="print-table-component">
    <ButtonWhite class="flex items-center gap-2" @click="handlePrint">
      <slot name="button-text">{{ $t('print.title') }}</slot>
      <PrintIcon />
    </ButtonWhite>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { typeLabel } from '@/services/typeLabels';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import PrintIcon from '@/assets/svg/PrintIcon.vue';

const { t, te } = useI18n();

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  fields: {
    type: Array,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  className: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['print-started', 'print-completed', 'print-failed']);

// Función para obtener el valor a mostrar, traduciendo los tipos de solicitud
const getDisplayValue = (item, fieldKey) => {
  const value = item[fieldKey];
  
  // Si es el campo "type", traducirlo igual que la tabla de escritorio (puede
  // traer varios valores, como los roles de Usuarios ADS)
  if (fieldKey === 'type' && value) {
    return typeLabel(value, item?.other_action, { t, te });
  }
  
  // Si es el campo "status" y tenemos un valor válido, traducirlo
  if (fieldKey === 'status' && value) {
    return t(`common.requestStatus.${value}`) || value;
  }
  
  return value || '-';
};

const handlePrint = () => {
  try {
    emit('print-started');
    
    // Crear una ventana de impresión
    const printWindow = window.open('', '_blank');
    
    // Crear el contenido HTML para imprimir
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${props.title}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            .print-title {
              text-align: center;
              font-size: 24px;
              margin-bottom: 20px;
              color: #385660;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .print-table th, .print-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .print-table th {
              background-color: #f2f2f2;
              color: #385660;
            }
            .print-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              @page {
                size: A4;
                margin: 1cm;
              }
              body {
                margin: 0;
              }
              .print-table {
                page-break-inside: auto;
              }
              .print-table tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
            }
          </style>
        </head>
        <body>
          <h1 class="print-title">${props.title}</h1>
          <table class="print-table">
            <thead>
              <tr>
                ${props.fields.map(field => `<th>${field.label}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${props.items.map(item => `
                <tr>
                  ${props.fields.map(field => `<td>${getDisplayValue(item, field.key)}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Escribir el contenido en la ventana
    printWindow.document.write(content);
    printWindow.document.close();

    // Esperar a que se cargue el contenido y luego imprimir
    printWindow.onload = function() {
      printWindow.print();
      printWindow.close();
      emit('print-completed');
    };

  } catch (error) {
    console.error('Error al imprimir:', error);
    emit('print-failed', error);
  }
};
</script>

<style scoped>
.print-table-component {
  display: inline-block;
}
</style> 